import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router";
import { Badge, Button, Input } from "@alascorporation/ui";
import { SateLabLogo } from "../components/common/SateLabLogo";

import {
  GridIcon,
  HorizontaLDots,
  VideoIcon,
  ShootingStarIcon,
  TaskIcon,
  ListIcon,
  BoxCubeIcon,
  FolderIcon,
  ChatIcon,
  PieChartIcon,
  CheckCircleIcon,
  DocsIcon,
  BoxIcon,
  PlugInIcon,
  CalenderIcon,
  TimeIcon,
  RefreshIcon,
  SproutIcon,
  RouteIcon,
  UserIcon,
  GroupIcon,
  UserCircleIcon,
  LockIcon,
  ChevronDownIcon,
  CopyIcon,
} from "../icons";
import { useSidebar } from "../context/sidebar";
import api from "../lib/api";
import { hasCurrentPolicy, type PermissionKey } from "../lib/auth";

type NavGroupItem = {
  name: string;
  icon: React.ReactNode;
  path: string;
  badge?: number;
  /** Omit for items every authenticated staff profile can reach (e.g. Dashboard). */
  policy?: PermissionKey;
  /** True for a link to another site (e.g. puntos.cccd.space) — renders a plain <a target="_blank"> instead of a react-router <Link>, since <Link> can't navigate off-site. */
  external?: boolean;
};

type NavSubGroup = {
  subLabel: string;
  items: NavGroupItem[];
};

type NavGroup = {
  label: string;
  items?: NavGroupItem[];       // flat (sin sub-grupos)
  subGroups?: NavSubGroup[];    // con sub-grupos colapsables
};

function normalize(value: string) {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
}

const AppSidebar: React.FC = () => {
  const { isExpanded, isMobileOpen, isHovered, setIsHovered } = useSidebar();
  const location = useLocation();
  const [pendingCount, setPendingCount] = useState(0);
  const [manualOverride, setManualOverride] = useState<Record<string, boolean>>({});
  const [query, setQuery] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  const isActive = useCallback(
    (path: string) => {
      if (path === "/programs/seedbeds")
        return (
          location.pathname === path ||
          (location.pathname.startsWith("/programs/seedbeds/") && !location.pathname.startsWith("/programs/seedbeds/stats"))
        );
      if (path === "/academy/courses")
        return location.pathname === path || location.pathname.startsWith("/academy/courses/");
      if (path === "/cccd/events")
        return location.pathname === path || location.pathname.startsWith("/cccd/events/");
      if (path === "/cccd/regionals")
        return location.pathname === path || location.pathname.startsWith("/cccd/regionals/");
      if (path === "/cccd") return location.pathname === "/cccd";
      if (path === "/tra/waitlist")
        return location.pathname === path || location.pathname.startsWith("/tra/waitlist/");
      if (path === "/tra/editions")
        return location.pathname === path || location.pathname.startsWith("/tra/editions/");
      if (path === "/tra/members")
        return location.pathname === path || location.pathname.startsWith("/tra/members/");
      if (path === "/instructor/groups")
        return location.pathname === path || location.pathname.startsWith("/instructor/groups/");
      return location.pathname === path;
    },
    [location.pathname]
  );

  useEffect(() => {
    api
      .get("/academy/projects/submissions/pending?limit=1")
      .then((res) => setPendingCount(res.data.data?.total ?? 0))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setManualOverride({});
    setQuery("");
  }, [location.pathname]);

  // Atajo de teclado: "/" enfoca la búsqueda de navegación desde cualquier parte de la página.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      const isTyping =
        target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable);
      if ((e.key === "/" && !isTyping) || ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k")) {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if (e.key === "Escape" && document.activeElement === searchRef.current) {
        setQuery("");
        searchRef.current?.blur();
      }
    }
    // El header pide el foco por evento; se espera un tick a que el menú colapsado termine de expandirse.
    function onFocusRequest() {
      setTimeout(() => searchRef.current?.focus(), 60)
    }
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("gestion:focus-nav-search", onFocusRequest);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("gestion:focus-nav-search", onFocusRequest);
    };
  }, []);

  const dashboardItem: NavGroupItem = useMemo(
    () => ({ name: "Inicio", icon: <GridIcon />, path: "/" }),
    []
  );

  const navGroups: NavGroup[] = useMemo(
    () => [
      {
        label: "Organizaciones y entidades",
        items: [
          { name: "Organizaciones", icon: <BoxCubeIcon />, path: "/programs/institutions", policy: "academy:institutions:view" },
          {
            name: "Instituciones educativas",
            icon: <FolderIcon />,
            path: "/cccd/academic-institutions",
            policy: "cccd:academic-institutions:view",
          },
        ],
      },
      {
        label: "Academia",
        subGroups: [
          {
            // Lo que un docente usa día a día: dar clase, calificar, hacer seguimiento.
            subLabel: "Docencia",
            items: [
              // "Mi grupo" va primero: es lo que el docente abre entre clases.
              { name: "Mi grupo", icon: <GroupIcon />, path: "/instructor/my-group", policy: "academy:my-group:view" },
              {
                name: "Panel del instructor",
                icon: <ChatIcon />,
                path: "/instructor/hub",
                badge: pendingCount > 0 ? pendingCount : undefined,
                policy: "academy:instructor-hub:view",
              },
              { name: "Entregas", icon: <TaskIcon />, path: "/instructor/submissions", policy: "academy:submissions:view" },
              { name: "Exámenes IA", icon: <ListIcon />, path: "/instructor/section-quizzes", policy: "academy:quizzes-teach:view" },
              { name: "Estudiantes", icon: <UserIcon />, path: "/instructor/students", policy: "academy:students:view" },
              { name: "Grupos", icon: <GroupIcon />, path: "/instructor/groups", policy: "academy:groups:view" },
              { name: "Estadísticas de docencia", icon: <PieChartIcon />, path: "/instructor/stats", policy: "academy:instructor-hub:view" },
            ],
          },
          {
            // Configuración de fondo que un administrador actualiza de vez en cuando, no a diario.
            subLabel: "Catálogo y estructura",
            items: [
              { name: "Programas académicos", icon: <RouteIcon />, path: "/programs/programs", policy: "academy:programs:view" },
              { name: "Cursos", icon: <DocsIcon />, path: "/academy/courses", policy: "academy:courses:view" },
              { name: "Videos", icon: <VideoIcon />, path: "/instructor/videos", policy: "academy:videos:view" },
              { name: "Semilleros", icon: <SproutIcon />, path: "/programs/seedbeds", policy: "academy:seedbeds:view" },
              { name: "Estadísticas de semilleros", icon: <PieChartIcon />, path: "/programs/seedbeds/stats", policy: "academy:seedbeds:view" },
              { name: "Certificados", icon: <CheckCircleIcon />, path: "/instructor/certificates", policy: "academy:certificates:view" },
            ],
          },
        ],
      },
      {
        label: "CCCD",
        subGroups: [
          {
            // Ítems que no pertenecen a un ámbito (nacional/regional) específico.
            subLabel: "General",
            items: [
              { name: "Panel CCCD", icon: <ShootingStarIcon />, path: "/cccd", policy: "cccd:panel:view" },
              { name: "Estadísticas de embudo", icon: <PieChartIcon />, path: "/cccd/funnel-stats", policy: "cccd:panel:view" },
              { name: "Catálogos", icon: <BoxIcon />, path: "/cccd/catalog-options", policy: "cccd:catalog-options:view" },
              { name: "Complementos", icon: <PlugInIcon />, path: "/cccd/addons", policy: "cccd:addons:view" },
              { name: "Expertos por categoría", icon: <UserIcon />, path: "/cccd/category-experts", policy: "cccd:panel:view" },
              { name: "Catálogo de construcción", icon: <BoxCubeIcon />, path: "/cccd/supply-catalog", policy: "cccd:supply-catalog:view" },
              { name: "Solicitudes de equipos", icon: <ListIcon />, path: "/cccd/supply-requests", policy: "cccd:supply-catalog:view" },
            ],
          },
          {
            subLabel: "Eventos",
            items: [
              // Check-in primero: el staff de puerta lo necesita en un toque, en el celular.
              { name: "Check-in / Asistencia", icon: <TaskIcon />, path: "/cccd/checkin", policy: "cccd:checkin:view" },
              { name: "Nacionales", icon: <CalenderIcon />, path: "/cccd/events", policy: "cccd:events-national:view" },
              { name: "Regionales", icon: <CalenderIcon />, path: "/cccd/regionals", policy: "cccd:events-regional:view" },
              { name: "Parámetros de evaluación", icon: <CopyIcon />, path: "/cccd/scoring-parameters", policy: "cccd:scoring:view" },
            ],
          },
        ],
      },
      {
        label: "Tripoli Colombia",
        subGroups: [
          {
            subLabel: "Programa",
            items: [
              { name: "Lista de Espera", icon: <TimeIcon />, path: "/tra/waitlist", policy: "tripoli:waitlist:view" },
              { name: "Estadísticas de TRA", icon: <PieChartIcon />, path: "/tra/stats", policy: "tripoli:waitlist:view" },
              { name: "Ediciones", icon: <RefreshIcon />, path: "/tra/editions", policy: "tripoli:editions:view" },
            ],
          },
          {
            subLabel: "Miembros",
            items: [
              { name: "Miembros certificados", icon: <UserIcon />, path: "/tra/members", policy: "tripoli:members:view" },
            ],
          },
        ],
      },
      {
        label: "Sistema",
        items: [
          { name: "Panel de visibilidad", icon: <PieChartIcon />, path: "/settings/visibility", policy: "admin:visibility-dashboard:view" },
          { name: "Cuentas de soporte", icon: <UserCircleIcon />, path: "/settings/support-accounts", policy: "support:accounts:view" },
          { name: "Usuarios", icon: <UserCircleIcon />, path: "/settings/users", policy: "staff:manage-accounts:view" },
          { name: "Roles y permisos", icon: <LockIcon />, path: "/settings/roles", policy: "staff:manage-roles:view" },
        ],
      },
    ],
    [pendingCount]
  );

  // RBAC: oculta cualquier item cuyo policy key el perfil actual no tenga. Un grupo/sub-grupo que
  // se queda sin items visibles desaparece entero — evita headers vacíos para Docente/Gestor CCCD.
  const policyFilteredGroups = useMemo(() => {
    const allowed = (item: NavGroupItem) => !item.policy || hasCurrentPolicy(item.policy);
    return navGroups
      .map((group) => {
        if (group.items) {
          const items = group.items.filter(allowed);
          return items.length > 0 ? { ...group, items } : null;
        }
        if (group.subGroups) {
          const subGroups = group.subGroups
            .map((sub) => {
              const items = sub.items.filter(allowed);
              return items.length > 0 ? { ...sub, items } : null;
            })
            .filter((sub): sub is NavSubGroup => sub !== null);
          return subGroups.length > 0 ? { ...group, subGroups } : null;
        }
        return null;
      })
      .filter((group): group is NonNullable<typeof group> => group !== null);
  }, [navGroups]);

  const trimmedQuery = query.trim();
  const isFiltering = trimmedQuery.length > 0;
  const normalizedQuery = normalize(trimmedQuery);

  const matchesQuery = useCallback(
    (name: string) => !isFiltering || normalize(name).includes(normalizedQuery),
    [isFiltering, normalizedQuery]
  );

  // Cuando se filtra, cada grupo/sub-grupo se reduce a los items que calzan; los vacíos se ocultan.
  const visibleGroups = useMemo(() => {
    if (!isFiltering) return policyFilteredGroups;
    return policyFilteredGroups
      .map((group) => {
        if (group.items) {
          const items = group.items.filter((item) => matchesQuery(item.name));
          return items.length > 0 ? { ...group, items } : null;
        }
        if (group.subGroups) {
          const subGroups = group.subGroups
            .map((sub) => {
              const items = sub.items.filter((item) => matchesQuery(item.name));
              return items.length > 0 ? { ...sub, items } : null;
            })
            .filter((sub): sub is NavSubGroup => sub !== null);
          return subGroups.length > 0 ? { ...group, subGroups } : null;
        }
        return null;
      })
      .filter((group): group is NonNullable<typeof group> => group !== null);
  }, [policyFilteredGroups, isFiltering, matchesQuery]);

  const dashboardVisible = matchesQuery(dashboardItem.name);

  const flatMatchCount = useMemo(
    () =>
      (dashboardVisible ? 1 : 0) +
      visibleGroups.reduce(
        (sum, g) => sum + (g.items?.length ?? 0) + (g.subGroups?.reduce((s, sg) => s + sg.items.length, 0) ?? 0),
        0
      ),
    [dashboardVisible, visibleGroups]
  );

  let activeKey: string | null = null;
  outer: for (const group of policyFilteredGroups) {
    if (!group.subGroups) continue;
    for (const sub of group.subGroups) {
      if (sub.items.some((item) => isActive(item.path))) {
        activeKey = `${group.label}::${sub.subLabel}`;
        break outer;
      }
    }
  }

  const isSubGroupOpen = (key: string) => isFiltering || (manualOverride[key] ?? key === activeKey);

  const toggle = (key: string) =>
    setManualOverride((prev) => ({ ...prev, [key]: !isSubGroupOpen(key) }));

  const showLabel = isExpanded || isHovered || isMobileOpen;

  const renderItems = (items: NavGroupItem[]) =>
    items.map((item) => {
      const active = !item.external && isActive(item.path);
      const content = (
        <>
          <span
            className={`relative menu-item-icon-size ${
              active ? "menu-item-icon-active" : "menu-item-icon-inactive"
            }`}
          >
            {item.icon}
            {!showLabel && item.badge !== undefined && item.badge > 0 && (
              <span className="absolute -right-0.5 -top-0.5 size-2 rounded-full bg-error-500" />
            )}
          </span>
          {showLabel && (
            <>
              <span className="menu-item-text">{item.name}</span>
              {item.badge !== undefined && item.badge > 0 && (
                <Badge
                  variant="destructive"
                  className="ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full px-1 text-[11px] font-bold"
                >
                  {item.badge > 99 ? "99+" : item.badge}
                </Badge>
              )}
            </>
          )}
        </>
      );

      return (
        <li key={item.name}>
          {item.external ? (
            <a
              href={item.path}
              target="_blank"
              rel="noopener noreferrer"
              className="menu-item group menu-item-inactive"
            >
              {content}
            </a>
          ) : (
            <Link
              to={item.path}
              aria-current={active ? "page" : undefined}
              className={`menu-item group ${active ? "menu-item-active" : "menu-item-inactive"}`}
            >
              {content}
            </Link>
          )}
        </li>
      );
    });

  return (
    <aside
      className={`fixed mt-16 flex flex-col lg:mt-0 top-0 px-5 left-0 bg-white dark:bg-gray-900 dark:border-gray-800 text-gray-900 h-screen transition-all duration-300 ease-in-out z-50 border-r border-gray-200
        ${isExpanded || isMobileOpen ? "w-[290px]" : isHovered ? "w-[290px]" : "w-[90px]"}
        ${isMobileOpen ? "translate-x-0" : "-translate-x-full"}
        lg:translate-x-0`}
      onMouseEnter={() => !isExpanded && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className={`py-8 flex ${
          !isExpanded && !isHovered ? "lg:justify-center" : "justify-start"
        }`}
      >
        <Link to="/">
          <SateLabLogo showLabel={showLabel} />
        </Link>
      </div>

      {showLabel && (
        <div className="pb-4">
          <div className="relative">
            <svg
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
              <path d="M21 21l-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <Input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar en el menú..."
              aria-label="Buscar en el menú de navegación"
              className="h-9 w-full rounded-lg border-gray-200 bg-gray-50 pl-9 pr-8 text-sm text-gray-700 placeholder:text-gray-400 focus-visible:border-brand-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/20 dark:border-gray-700 dark:bg-white/[0.03] dark:text-gray-300"
            />
            {isFiltering ? (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setQuery("")}
                aria-label="Limpiar búsqueda"
                className="absolute right-2 top-1/2 flex size-5 -translate-y-1/2 items-center justify-center rounded text-gray-400 outline-none hover:bg-gray-200 hover:text-gray-600 focus-visible:ring-2 focus-visible:ring-brand-500/40 dark:hover:bg-white/10"
              >
                <svg viewBox="0 0 24 24" fill="none" className="size-3.5">
                  <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </Button>
            ) : (
              <kbd className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded border border-gray-200 bg-white px-1.5 py-0.5 text-[11px] font-medium text-gray-500 dark:text-gray-400 dark:border-gray-700 dark:bg-gray-900">
                /
              </kbd>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-1 flex-col overflow-y-auto duration-300 ease-linear no-scrollbar">
        <nav className="mb-6" aria-label="Navegación principal">
          {isFiltering && showLabel && (
            <p className="mb-3 px-1 text-xs text-gray-500 dark:text-gray-400">
              {flatMatchCount > 0
                ? `${flatMatchCount} resultado${flatMatchCount === 1 ? "" : "s"}`
                : "Sin resultados"}
            </p>
          )}
          <div className="flex flex-col gap-6">
            {dashboardVisible && (
              <ul className="flex flex-col gap-2">{renderItems([dashboardItem])}</ul>
            )}
            {visibleGroups.map((group) => (
              <div key={group.label}>
                {/* Group label */}
                <h2
                  className={`mb-3 text-xs uppercase flex leading-[20px] text-gray-500 dark:text-gray-400 ${
                    !isExpanded && !isHovered ? "lg:justify-center" : "justify-start"
                  }`}
                >
                  {showLabel ? group.label : <HorizontaLDots className="size-6" />}
                </h2>

                {/* Flat items (Organizaciones y entidades, Sistema) */}
                {group.items && (
                  <ul className="flex flex-col gap-2">{renderItems(group.items)}</ul>
                )}

                {/* Sub-groups (Academia) */}
                {group.subGroups && (
                  <div className="flex flex-col gap-1">
                    {group.subGroups.map((sub) => {
                      const key = `${group.label}::${sub.subLabel}`;
                      const isOpen = isSubGroupOpen(key);

                      return (
                        <div key={sub.subLabel}>
                          {/* Sub-group header — only visible in expanded mode */}
                          {showLabel ? (
                            <Button
                              variant="ghost"
                              onClick={() => toggle(key)}
                              aria-expanded={isOpen}
                              className="flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 outline-none hover:bg-gray-100 focus-visible:ring-2 focus-visible:ring-brand-500/40 dark:hover:bg-white/5"
                            >
                              <span className="flex-1 text-left">{sub.subLabel}</span>
                              <span
                                className={`menu-item-arrow transition-transform duration-200 ${
                                  isOpen ? "menu-item-arrow-active" : "menu-item-arrow-inactive"
                                }`}
                              >
                                <ChevronDownIcon className="size-4" />
                              </span>
                            </Button>
                          ) : null}

                          {/* Sub-group items */}
                          {(isOpen || !showLabel) && (
                            <ul className={`flex flex-col gap-2 ${showLabel ? "mt-1 pl-2" : ""}`}>
                              {renderItems(sub.items)}
                            </ul>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </nav>
      </div>

      {showLabel && (
        <div className="mt-auto flex items-center justify-between border-t border-gray-100 px-1 py-3 text-xs text-gray-500 dark:border-gray-800 dark:text-gray-500">
          <span>Versión: v{__APP_VERSION__}</span>
          <span>satelab.org</span>
        </div>
      )}
    </aside>
  );
};

export default AppSidebar;
